/**
 * Web adapter: maps @amazing-wedding/ai-planner tool port to existing Firestore ops.
 */

import type { PlannerOperations } from "@amazing-wedding/ai-planner";
import { PlanRequiredError as PlannerPlanRequiredError } from "@amazing-wedding/ai-planner";
import { addBudgetItem, deleteBudgetItem, updateBudgetItem } from "@/features/budget/operations";
import {
  createDayPlanEvent,
  deleteDayPlanEvent,
  updateDayPlanEvent,
} from "@/features/day-plan/operations";
import { createGuest, softDeleteGuest, updateGuest } from "@/features/guests/operations";
import { projectCreatedGuest, syncAttendeeCount } from "@/features/guests/sync-attendee-count";
import { ensureRsvpLink, rsvpLinkUrl } from "@/features/rsvp/operations";
import {
  assignGuestToSeat,
  createTable,
  deleteTable,
  moveGuestWithinTable,
  unassignGuestWithSeatMap,
  updateTable,
} from "@/features/seating/operations";
import { setupSeatingPlan } from "@/features/seating/setup-seating-plan";
import { createTask, deleteTask, updateTask } from "@/features/tasks/operations";
import {
  PlanRequiredError as WebPlanRequiredError,
  updateWebsiteConfig as webUpdateWebsiteConfig,
} from "@/features/wedding/operations";
import { addMemoryNote, loadPlannerMemory, removeMemoryNote } from "./memory/operations";

export const webPlannerOperations: PlannerOperations = {
  guests: {
    createGuest,
    updateGuest,
    softDeleteGuest,
    projectCreatedGuest,
    syncAttendeeCount: (weddingId, linked) => syncAttendeeCount(weddingId, linked ?? []),
  },
  tasks: {
    createTask,
    updateTask,
    deleteTask,
  },
  budget: {
    addBudgetItem,
    updateBudgetItem,
    deleteBudgetItem,
  },
  dayPlan: {
    createDayPlanEvent,
    updateDayPlanEvent,
    deleteDayPlanEvent,
  },
  seating: {
    assignGuestToSeat,
    createTable,
    deleteTable,
    moveGuestWithinTable,
    unassignGuestWithSeatMap,
    updateTable,
    setupSeatingPlan,
  },
  memory: {
    loadPlannerMemory,
    addMemoryNote,
    removeMemoryNote,
  },
  website: {
    updateWebsiteConfig: async (weddingId, patch) => {
      try {
        await webUpdateWebsiteConfig(weddingId, patch);
      } catch (err) {
        if (err instanceof WebPlanRequiredError) {
          throw new PlannerPlanRequiredError(err.message);
        }
        throw err;
      }
    },
    PlanRequiredError: PlannerPlanRequiredError,
  },
  rsvp: {
    ensureRsvpLink,
    rsvpLinkUrl,
  },
};
